import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import multiMonthPlugin from "@fullcalendar/multimonth";
import type {DatesSetArg} from "@fullcalendar/core/index.js";
import FullCalendar from "@fullcalendar/react";
import {memo, type RefObject} from "react";
import type {BeatPlanningCalendarEvent} from "~/components/beatPlanningCalendar/beatPlanningCalendar";
import type {YearDayStats} from "~/components/beatPlanningCalendar/useCalendarStats";

function toLocalDayKey(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function MemoizedCalendarYearView({
    isMobile,
    yearCalendarRef,
    calendarRootRef,
    events,
    eventDayKeys,
    yearViewDayStats,
    onYearDatesSet,
    onYearEventsSet,
    openDayEventsModal,
    onEventSelected,
    eventOpensDaySummaryModal = false,
}: {
    isMobile: boolean;
    yearCalendarRef: RefObject<FullCalendar | null>;
    calendarRootRef: RefObject<HTMLDivElement | null>;
    events: Array<
        BeatPlanningCalendarEvent & {
            backgroundColor?: string;
            borderColor?: string;
            textColor?: string;
        }
    >;
    eventDayKeys: Set<string>;
    yearViewDayStats: Map<string, YearDayStats>;
    onYearDatesSet: (args: DatesSetArg) => void;
    onYearEventsSet: () => void;
    openDayEventsModal: (date: Date, calendarInstanceRef: RefObject<FullCalendar | null>) => void;
    onEventSelected: (event: {
        id: string;
        title: string;
        start: Date | null;
        end: Date | null;
        allDay: boolean;
        extendedProps: unknown;
        backgroundColor?: string;
        borderColor?: string;
        textColor?: string;
    }) => void;
    eventOpensDaySummaryModal?: boolean;
}) {
    return (
        <div
            ref={calendarRootRef}
            className="beat-planning-calendar__calendar-shell"
            data-view="multiMonthYear"
        >
            <FullCalendar
                ref={yearCalendarRef}
                plugins={[dayGridPlugin, multiMonthPlugin, interactionPlugin]}
                initialView="multiMonthYear"
                height="auto"
                views={{
                    multiMonthYear: {
                        eventDisplay: "none",
                        showNonCurrentDates: true,
                        fixedWeekCount: false,
                    },
                }}
                multiMonthTitleFormat={{month: "long", year: "numeric"}}
                headerToolbar={false}
                datesSet={onYearDatesSet}
                eventsSet={onYearEventsSet}
                events={events}
                dayHeaderContent={(arg) => {
                    const weekday = arg.date.toLocaleDateString(undefined, {weekday: "short"});
                    return (
                        <span className="beat-planning-calendar__year-mini-weekday">{weekday}</span>
                    );
                }}
                dayCellContent={(arg) => {
                    const dayKey = toLocalDayKey(arg.date);
                    const stats = yearViewDayStats.get(dayKey);
                    const count = stats?.count ?? 0;
                    const statusLine = stats?.statusLine ?? "";
                    return (
                        <div className="beat-planning-calendar__year-day-inner">
                            <span className="beat-planning-calendar__year-day-number-row">
                                <span
                                    className={
                                        arg.isToday
                                            ? "beat-planning-calendar__year-day-number beat-planning-calendar__year-day-number--today"
                                            : "beat-planning-calendar__year-day-number"
                                    }
                                >
                                    {arg.dayNumberText}
                                </span>
                                {count > 0 ? (
                                    <span
                                        className="beat-planning-calendar__year-day-badge-wrap"
                                        tabIndex={0}
                                        aria-label={statusLine === "" ? undefined : `Events status: ${statusLine}`}
                                        data-status-tooltip={statusLine === "" ? undefined : `Events status:\n${statusLine}`}
                                    >
                                        <span className="beat-planning-calendar__year-day-badge">{count}</span>
                                    </span>
                                ) : null}
                            </span>
                        </div>
                    );
                }}
                dayCellClassNames={(arg) => {
                    const dayKey = toLocalDayKey(arg.date);
                    return eventDayKeys.has(dayKey)
                        ? "beat-planning-calendar__day-cell--has-events"
                        : "beat-planning-calendar__day-cell--empty";
                }}
                moreLinkClick={(info) => {
                    openDayEventsModal(info.date, yearCalendarRef);
                    return true as unknown as undefined;
                }}
                dateClick={(info) => {
                    openDayEventsModal(info.date, yearCalendarRef);
                }}
                eventClick={(info) => {
                    if (eventOpensDaySummaryModal === true && info.event.start !== null) {
                        openDayEventsModal(info.event.start, yearCalendarRef);
                        return;
                    }
                    onEventSelected({
                        id: info.event.id,
                        title: info.event.title,
                        start: info.event.start,
                        end: info.event.end,
                        allDay: info.event.allDay,
                        extendedProps: info.event.extendedProps,
                        backgroundColor: info.event.backgroundColor,
                        borderColor: info.event.borderColor,
                        textColor: info.event.textColor,
                    });
                }}
                dayMaxEventRows={isMobile === true ? 2 : true}
                dayMaxEvents={isMobile === true ? 2 : true}
            />
        </div>
    );
}

export const CalendarYearView = memo(MemoizedCalendarYearView);
